/**
 * What a posted bill still owes, and the three ways to bring it down here:
 * pay it through a payment request, set the vendor's advance against it, or a
 * credit or debit note.
 *
 * The figures come from the server's own position — the payable is what the
 * bill's posting credited to payables (the total less TDS), so what this panel
 * shows is what the aged payables list and the ledger say.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Note, Pill } from './ui'

interface Clearance {
  kind: string
  amount_paise: number
  cleared_on: string
  reason: string | null
  reversed: boolean
  source_reference: string | null
  journal_number: string | null
  reversal_reason: string | null
}

interface NoteRow {
  id: string
  reference: string
  kind: string
  note_no: string
  note_date: string
  reason: string
  taxable_paise: number
  cgst_paise: number
  sgst_paise: number
  igst_paise: number
  total_paise: number
  status: string
  approval_state: string
  created_by_name: string | null
}

interface Position {
  invoice_id: string
  reference: string
  status: string
  total_paise: number
  tds_paise: number
  payable_paise: number
  paid_by_runs_paise: number
  clearances: Clearance[]
  outstanding_paise: number
  advance_available_paise: number
  payment_request_id: string | null
  payment_request_reference: string | null
  payment_request_status: string | null
  notes: NoteRow[]
  paid_paise: number
  due_date: string | null
  payment_status: string | null
  //: Bank payments prepared against the bill, waiting for the Finance Head.
  held_paise: number
}

const CLEARANCE_LABELS: Record<string, string> = {
  vendor_payment: 'Paid from the bank',
  payment_request: 'Paid through a payment request',
  advance_adjustment: 'Advance set against it',
  credit_note: 'Credit or debit note',
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  return clean ? Math.round(Number(clean) * 100) : 0
}

const today = () => new Date().toISOString().slice(0, 10)

export default function BillPosition({
  invoiceId,
  onClose,
}: {
  invoiceId: string
  onClose: () => void
}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const key = ['bill-position', invoiceId]
  const position = useQuery({
    queryKey: key,
    queryFn: () => api.get<Position>(`/procurement/invoices/${invoiceId}/position`),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key })
    void queryClient.invalidateQueries({ queryKey: ['invoices'] })
    void queryClient.invalidateQueries({ queryKey: ['payment-requests'] })
  }

  const raise = useMutation({
    mutationFn: () => api.post<Position>(`/procurement/invoices/${invoiceId}/payment-request`),
    onSuccess: refresh,
  })
  const [adjust, setAdjust] = useState('')
  const adjustAdvance = useMutation({
    mutationFn: () =>
      api.post<Position>(`/procurement/invoices/${invoiceId}/adjust-advance`, {
        amount_paise: toPaise(adjust),
      }),
    onSuccess: () => {
      setAdjust('')
      refresh()
    },
  })
  const submitNote = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/credit-notes/${id}/submit`),
    onSuccess: refresh,
  })
  const postNote = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/credit-notes/${id}/post`),
    onSuccess: refresh,
  })
  const [noteOpen, setNoteOpen] = useState(false)

  const p = position.data
  return (
    <Card
      title={p ? `${p.reference} — what it still owes` : 'Bill balance'}
      actions={
        <button className="small" onClick={onClose}>
          Close
        </button>
      }
    >
      <ErrorNote
        error={
          position.error ?? raise.error ?? adjustAdvance.error ?? submitNote.error ?? postNote.error
        }
      />
      {!p ? null : (
        <>
          <div className="row">
            <div>
              <div className="caption">Bill total</div>
              {formatInr(p.total_paise)}
            </div>
            <div>
              <div className="caption">TDS withheld</div>
              {p.tds_paise ? formatInr(p.tds_paise) : <span className="faint">none</span>}
            </div>
            <div>
              <div className="caption">Owed to the vendor</div>
              {formatInr(p.payable_paise)}
            </div>
            <div>
              <div className="caption">Paid by payment runs</div>
              {formatInr(p.paid_by_runs_paise)}
            </div>
            <div>
              <div className="caption">Paid in all</div>
              {formatInr(p.paid_paise)}
            </div>
            <div>
              <div className="caption">Still owed</div>
              <b>{formatInr(p.outstanding_paise)}</b>
              {p.payment_status && (
                <div>
                  <Pill value={p.payment_status} />
                </div>
              )}
            </div>
            {p.due_date && (
              <div>
                <div className="caption">Due</div>
                {p.due_date}
              </div>
            )}
          </div>
          {p.held_paise > 0 && (
            <Note kind="info">
              {formatInr(p.held_paise)} of it is in a bank payment waiting for the Finance Head —
              not paid yet, but no longer free to pay again.
            </Note>
          )}

          {p.clearances.length > 0 && (
            <>
              <h3 style={{ marginBottom: 4 }}>Payment history</h3>
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>How</th>
                    <th>Reference</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {p.clearances.map((c, i) => (
                    <tr
                      key={i}
                      style={{ textDecoration: c.reversed ? 'line-through' : undefined }}
                      title={c.reversed ? `Reversed: ${c.reversal_reason ?? ''}` : undefined}
                    >
                      <td className="muted date">{c.cleared_on}</td>
                      <td>
                        {CLEARANCE_LABELS[c.kind] ?? c.kind}
                        {c.reason && <div className="cell-sub">{c.reason}</div>}
                      </td>
                      <td>
                        {c.source_reference && <span className="ref">{c.source_reference}</span>}
                        {c.journal_number && (
                          <div className="cell-sub mono">{c.journal_number}</div>
                        )}
                      </td>
                      <td className="num">
                        {formatInr(c.amount_paise)}
                        {c.reversed && <div className="cell-sub">reversed</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {p.status === 'posted' && (
            <>
              <h3 style={{ marginBottom: 4 }}>Pay it</h3>
              {p.outstanding_paise > 0 && (
                <p className="faint">
                  Paid already by net banking? Open the payment on{' '}
                  <Link to="/books/banking">Bank statements</Link> and use <b>Pay bills</b> — part
                  of this bill, or this and others in one go.
                </p>
              )}
              {p.payment_request_reference ? (
                <Note kind="info">
                  Being paid by{' '}
                  <Link to="/spending/payment-requests">{p.payment_request_reference}</Link> (
                  {p.payment_request_status?.replace(/_/g, ' ')}).
                </Note>
              ) : p.outstanding_paise > 0 && can('spend.payment_request.create') ? (
                <p className="muted">
                  <button
                    className="primary small"
                    disabled={raise.isPending}
                    onClick={() => raise.mutate()}
                  >
                    Raise payment request for {formatInr(p.outstanding_paise)}
                  </button>{' '}
                  It carries this bill's approval, for this vendor and this amount, and is settled
                  against the bank line when the Finance Head has paid it.
                </p>
              ) : (
                p.outstanding_paise <= 0 && <p className="faint">Nothing left to pay.</p>
              )}

              {p.advance_available_paise > 0 &&
                p.outstanding_paise > 0 &&
                can('ap.payment.prepare') && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      adjustAdvance.mutate()
                    }}
                  >
                    <h3 style={{ marginBottom: 4 }}>Set an advance against it</h3>
                    <p className="muted" style={{ marginTop: 0 }}>
                      This vendor holds {formatInr(p.advance_available_paise)} of our money as an
                      advance.
                    </p>
                    <div className="row">
                      <Field label="Amount (₹)">
                        <input
                          className="num"
                          value={adjust}
                          onChange={(e) => setAdjust(e.target.value)}
                          placeholder={(
                            Math.min(p.advance_available_paise, p.outstanding_paise) / 100
                          ).toFixed(2)}
                          required
                        />
                      </Field>
                      <div className="shrink">
                        <button className="small" type="submit" disabled={adjustAdvance.isPending}>
                          Adjust advance
                        </button>
                      </div>
                    </div>
                  </form>
                )}

              <h3 style={{ marginBottom: 4 }}>Credit and debit notes</h3>
              {p.notes.length === 0 ? (
                <p className="faint">None.</p>
              ) : (
                <table style={{ marginBottom: 8 }}>
                  <thead>
                    <tr>
                      <th>Note</th>
                      <th>Reason</th>
                      <th className="num">Total</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {p.notes.map((n) => (
                      <tr key={n.id}>
                        <td className="mono">
                          {n.reference}
                          <div className="faint">
                            {n.kind.replace('_', ' ')} {n.note_no}, {n.note_date}
                          </div>
                        </td>
                        <td className="muted">{n.reason}</td>
                        <td className="num">{formatInr(n.total_paise)}</td>
                        <td>
                          <Pill value={n.status} />
                          {n.status === 'submitted' && (
                            <div className="faint" style={{ fontSize: 12 }}>
                              {n.approval_state} — decided from Approvals
                            </div>
                          )}
                        </td>
                        <td className="num">
                          {n.status === 'draft' && can('ap.invoice.submit') && (
                            <button className="small" onClick={() => submitNote.mutate(n.id)}>
                              Submit
                            </button>
                          )}
                          {n.status === 'approved' && can('ap.invoice.create') && (
                            <button className="small primary" onClick={() => postNote.mutate(n.id)}>
                              Post
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {can('ap.invoice.create') &&
                (noteOpen ? (
                  <NoteForm
                    invoiceId={invoiceId}
                    onCancel={() => setNoteOpen(false)}
                    onDone={() => {
                      setNoteOpen(false)
                      refresh()
                    }}
                  />
                ) : (
                  <button className="small" onClick={() => setNoteOpen(true)}>
                    Record a credit or debit note
                  </button>
                ))}
            </>
          )}
        </>
      )}
    </Card>
  )
}

function NoteForm({
  invoiceId,
  onCancel,
  onDone,
}: {
  invoiceId: string
  onCancel: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({
    kind: 'credit_note',
    note_no: '',
    note_date: today(),
    reason: '',
    taxable: '',
    cgst: '',
    sgst: '',
    igst: '',
  })
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch })
  const save = useMutation({
    mutationFn: () =>
      api.post(`/procurement/invoices/${invoiceId}/credit-notes`, {
        kind: form.kind,
        note_no: form.note_no,
        note_date: form.note_date,
        reason: form.reason,
        taxable_paise: toPaise(form.taxable),
        cgst_paise: toPaise(form.cgst),
        sgst_paise: toPaise(form.sgst),
        igst_paise: toPaise(form.igst),
      }),
    onSuccess: onDone,
  })
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ErrorNote error={save.error} />
      <div className="row">
        <Field label="Kind">
          <select value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
            <option value="credit_note">Credit note from the vendor</option>
            <option value="debit_note">Debit note we raise</option>
          </select>
        </Field>
        <Field label="Number">
          <input value={form.note_no} onChange={(e) => set({ note_no: e.target.value })} required />
        </Field>
        <Field label="Dated">
          <input
            type="date"
            value={form.note_date}
            onChange={(e) => set({ note_date: e.target.value })}
            required
          />
        </Field>
        <Field label="Why">
          <input
            value={form.reason}
            onChange={(e) => set({ reason: e.target.value })}
            placeholder="short supply, goods returned, price agreed lower"
            required
          />
        </Field>
      </div>
      <div className="row">
        <Field label="Taxable (₹)">
          <input
            className="num"
            value={form.taxable}
            onChange={(e) => set({ taxable: e.target.value })}
            required
          />
        </Field>
        <Field label="CGST (₹)">
          <input
            className="num"
            value={form.cgst}
            onChange={(e) => set({ cgst: e.target.value })}
          />
        </Field>
        <Field label="SGST (₹)">
          <input
            className="num"
            value={form.sgst}
            onChange={(e) => set({ sgst: e.target.value })}
          />
        </Field>
        <Field label="IGST (₹)">
          <input
            className="num"
            value={form.igst}
            onChange={(e) => set({ igst: e.target.value })}
          />
        </Field>
      </div>
      <Note kind="info">
        Saved as a draft. Submit it, have it approved from Approvals by someone else, then post it:
        it credits what the bill charged, reverses the input credit, and reduces what the bill owes.
      </Note>
      <div className="row">
        <div className="shrink">
          <button className="primary small" type="submit" disabled={save.isPending}>
            Save note
          </button>{' '}
          <button type="button" className="small" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}
